import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { RedriveConfig } from "../Components/redrive/RedriveConfigForm";
import { RedriveOptions, RedriveStartResult, RedrivePollResult } from "../services/RedriveService";
import { RealmLower, NotifyFn, NAV } from "../constants";
import { HashParams, parseRealm, toErrorMessage } from "../utils";
import { useMountedRef } from "./useMountedRef";

const DEFAULT_CONFIG: RedriveConfig = {
  stateMachineName: "",
  realm: "NA",
  override: true,
  resume: true,
  redriveFailed: true,
  redriveAborted: true,
  redriveTimedOut: true,
  redriveRunning: false,
  startDate: "",
  endDate: "",
};

const POLL_INTERVAL_MS = 2000;

export const parseUrlConfig = (): Partial<RedriveConfig> => {
  const params = HashParams.parse();
  if (params.toString() === "") {
    return {};
  }
  return {
    realm: parseRealm(params.get("realm")),
    stateMachineName: params.get("stateMachine") || undefined,
    override: params.get("override") === "true",
    resume: params.get("resume") === "true",
    redriveFailed: params.get("redriveFailed") === "true",
    redriveAborted: params.get("redriveAborted") === "true",
    redriveTimedOut: params.get("redriveTimedOut") === "true",
    redriveRunning: params.get("redriveRunning") === "true",
    startDate: params.get("startDate") || undefined,
    endDate: params.get("endDate") || undefined,
  };
};

export const updateUrl = (config: RedriveConfig): void => {
  if (!config.stateMachineName) {
    return;
  }
  HashParams.update(NAV.ROUTES.REDRIVE, {
    realm: config.realm.toLowerCase(),
    stateMachine: config.stateMachineName,
    override: String(config.override),
    resume: String(config.resume),
    redriveFailed: String(config.redriveFailed),
    redriveAborted: String(config.redriveAborted),
    redriveTimedOut: String(config.redriveTimedOut),
    redriveRunning: String(config.redriveRunning),
    startDate: config.startDate,
    endDate: config.endDate,
  });
};

export interface UseRedriveProps {
  onNotify: NotifyFn;
  startRedriveFn: (realm: RealmLower, params: RedriveOptions) => Promise<RedriveStartResult>;
  pollRedriveFn: (realm: RealmLower, arn: string, logGroup: string, logStream: string) => Promise<RedrivePollResult>;
  buildArnFn: (realm: RealmLower, name: string) => string;
}

export interface UseRedriveReturn {
  config: RedriveConfig;
  setConfig: (c: RedriveConfig) => void;
  loading: boolean;
  polling: boolean;
  pollResult: RedrivePollResult | null;
  handleRedrive: () => Promise<void>;
  handleCancel: () => void;
}

export function useRedrive({ onNotify, startRedriveFn, pollRedriveFn, buildArnFn }: UseRedriveProps): UseRedriveReturn {
  const initialConfig = useMemo(() => ({ ...DEFAULT_CONFIG, ...parseUrlConfig() }), []);
  const [config, setConfig] = useState<RedriveConfig>(initialConfig);
  const [loading, setLoading] = useState(false);
  const [polling, setPolling] = useState(false);
  const [pollResult, setPollResult] = useState<RedrivePollResult | null>(null);
  const cancelRef = useRef(false);
  const inFlightRef = useRef(false);
  const mountedRef = useMountedRef();

  useEffect(
    () => () => {
      cancelRef.current = true;
    },
    []
  );

  useEffect(() => {
    if (config.stateMachineName) {
      updateUrl(config);
    }
  }, [config]);

  const pollForResult = useCallback(
    async (start: RedriveStartResult, realmLower: RealmLower): Promise<void> => {
      setPolling(true);
      while (!cancelRef.current) {
        try {
          const result = await pollRedriveFn(realmLower, start.executionArn, start.logGroupName, start.logStreamName);
          if (!mountedRef.current) {
            return;
          }
          setPollResult(result);
          if (result.redriveByDatesResult || result.workflowError) {
            break;
          }
          await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
        } catch (err) {
          onNotify("error", `Polling failed: ${toErrorMessage(err)}`);
          break;
        }
      }
      if (mountedRef.current) {
        setPolling(false);
      }
    },
    [pollRedriveFn, onNotify, mountedRef]
  );

  const handleRedrive = useCallback(async (): Promise<void> => {
    if (inFlightRef.current) {
      return;
    }
    if (!config.stateMachineName.trim()) {
      onNotify("error", "State machine name is required");
      return;
    }

    inFlightRef.current = true;
    setLoading(true);
    setPollResult(null);
    cancelRef.current = false;

    try {
      const realmLower = config.realm.toLowerCase() as RealmLower;
      const stateMachineArn = buildArnFn(realmLower, config.stateMachineName);
      const result = await startRedriveFn(realmLower, {
        stateMachineArn,
        override: config.override,
        resume: config.resume,
        redriveFailed: config.redriveFailed,
        redriveAborted: config.redriveAborted,
        redriveTimedOut: config.redriveTimedOut,
        redriveRunning: config.redriveRunning,
        startDate: config.startDate ? new Date(config.startDate) : undefined,
        endDate: config.endDate ? new Date(config.endDate) : undefined,
      });
      if (!mountedRef.current) {
        return;
      }
      await pollForResult(result, realmLower);
    } catch (err) {
      onNotify("error", `Redrive failed: ${toErrorMessage(err)}`);
    } finally {
      inFlightRef.current = false;
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [config, onNotify, startRedriveFn, buildArnFn, pollForResult, mountedRef]);

  const handleCancel = useCallback((): void => {
    cancelRef.current = true;
  }, []);

  return { config, setConfig, loading, polling, pollResult, handleRedrive, handleCancel };
}
